import React, { useState } from "react";
import { createClientPayment, uploadFile } from "../api";

const paymentMethods = [
  { id: "efectivo", label: "Efectivo" },
  { id: "transferencia", label: "Transferencia" },
  { id: "tarjeta", label: "Tarjeta" },
  { id: "cheque", label: "Cheque" },
];

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const formatMoney = (value) => Number(value || 0).toLocaleString("de-DE");

const ClientPaymentModal = ({ client, summary, onClose, onSaved, canUpdateClient }) => {
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    method: "",
    reference: "",
    paidAt: getTodayDateString(),
    note: "",
    voucherFile: null,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!client) return null;

  const balance = Number(summary?.balance || 0);
  const amountValue = Number(paymentForm.amount || 0);
  const remaining = Math.max(balance - amountValue, 0);
  const overpay = amountValue > balance ? amountValue - balance : 0;

  const handleChange = (field, value) => {
    setPaymentForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!canUpdateClient) return;
    setError("");
    const amount = Number(paymentForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Ingresa un monto valido.");
      return;
    }
    if (!paymentForm.method) {
      setError("Selecciona un metodo de pago.");
      return;
    }

    setSubmitting(true);
    try {
      let voucherUrl;
      if (paymentForm.voucherFile) {
        const response = await uploadFile(paymentForm.voucherFile);
        voucherUrl = response?.url;
      }

      const payload = {
        amount,
        method: paymentForm.method,
        reference: paymentForm.reference || undefined,
        paidAt: paymentForm.paidAt || undefined,
        note: paymentForm.note || undefined,
      };
      if (voucherUrl) {
        payload.voucherUrl = voucherUrl;
      }

      await createClientPayment(client.id, payload);
      if (onSaved) {
        await onSaved();
      }
      onClose();
    } catch (err) {
      console.error(err);
      setError("Error al registrar el pago.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Registro de pagos
            </p>
            <h2 className="text-xl font-semibold text-slate-900">Registrar pago</h2>
            <p className="text-sm text-slate-600">{client.name}</p>
          </div>
          <button
            className="text-slate-500 hover:text-slate-900"
            onClick={onClose}
            type="button"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Saldo pendiente</p>
              <p className="text-lg font-semibold text-slate-900">${formatMoney(balance)}</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-emerald-700">Saldo restante</p>
              <p className="text-lg font-semibold text-emerald-800">${formatMoney(remaining)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Credito a favor</p>
              <p className="text-lg font-semibold text-slate-900">
                ${formatMoney(overpay > 0 ? overpay : 0)}
              </p>
            </div>
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1 text-sm text-slate-600">
              <span className="font-medium text-slate-700">Monto</span>
              <input
                type="number"
                min="0"
                value={paymentForm.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                placeholder="Ej: 25.000"
                disabled={!canUpdateClient}
              />
            </label>
            <label className="space-y-1 text-sm text-slate-600">
              <span className="font-medium text-slate-700">Metodo de pago</span>
              <select
                value={paymentForm.method}
                onChange={(e) => handleChange("method", e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                disabled={!canUpdateClient}
              >
                <option value="">Selecciona un metodo</option>
                {paymentMethods.map((method) => (
                  <option key={method.id} value={method.id}>
                    {method.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm text-slate-600">
              <span className="font-medium text-slate-700">Referencia</span>
              <input
                type="text"
                value={paymentForm.reference}
                onChange={(e) => handleChange("reference", e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                placeholder="Transferencia, cheque, etc."
                disabled={!canUpdateClient}
              />
            </label>
            <label className="space-y-1 text-sm text-slate-600">
              <span className="font-medium text-slate-700">Fecha de pago</span>
              <input
                type="date"
                value={paymentForm.paidAt}
                onChange={(e) => handleChange("paidAt", e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                disabled={!canUpdateClient}
              />
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1 text-sm text-slate-600 sm:col-span-2">
              <span className="font-medium text-slate-700">Nota</span>
              <textarea
                value={paymentForm.note}
                onChange={(e) => handleChange("note", e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                rows={3}
                placeholder="Observaciones o detalles del pago."
                disabled={!canUpdateClient}
              />
            </label>
            <label className="space-y-1 text-sm text-slate-600 sm:col-span-2">
              <span className="font-medium text-slate-700">Comprobante</span>
              <input
                type="file"
                onChange={(e) => handleChange("voucherFile", e.target.files?.[0] || null)}
                className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm"
                disabled={!canUpdateClient}
              />
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
            type="button"
          >
            Cancelar
          </button>
          {canUpdateClient && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
              type="button"
            >
              Guardar pago
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientPaymentModal;
